<template>
  <v-layout row wrap>
    <v-flex xl4 lg5 md12 sm12 class="card-border-right">
      <v-layout row column :reverse="$vuetify.breakpoint.smAndDown">
        <StationComponents ref="stationComponents" :disabled="disabledStates.stations || !isInitialized" :selection="selection" :analyteStations="analyteStations" @stations="selection.stations = $event" @loading="loadingStates.stations = $event" :stationsMediaTypes="stationsMediaTypes" @mediaTypes="selection.stationsMediaTypes = $event" />
        <v-divider />
        <DateRange ref="dateRange" :disabled="disabledStates.dateRange" @dateRange="selection.dateRange = $event" :availableDateRange="currentDateRange" />
        <v-divider />
        <Guidelines ref="guidelines" :disabled="disabledStates.guidelines" :selection="selection" :guidelines="guidelines" @mediaTypes="guidelinesMediaTypes = $event" @guidelines="selection.guidelines = $event" />
      </v-layout>
    </v-flex>
    <v-flex xl8 lg7 md12 sm12 class="card-bg-2 card-border-left">
      <Analytes ref="analytes" :disabled="disabledStates.analytes" @analytes="updateAnalytesSelection($event);" :allAnalytes="filteredAllAnalytes" :analytes="filteredAnalytes" :selection="selection" />
    </v-flex>
  </v-layout>
</template>

<script>
  import {
    createNamespacedHelpers,
  } from 'vuex'
  const app = createNamespacedHelpers('app')
  import StationComponents from './Parameters/StationComponents/StationComponents'
  import Guidelines from './Parameters/Guidelines/Guidelines'
  import Analytes from './Parameters/Analytes/Analytes'
  import DateRange from './Parameters/DateRange/DateRange'

  import {
    StationMediaTypeGrouping,
    analytesDateRange,
  } from '@/helpers/multiStationQuery'
  import { debounce } from '@/helpers/performance'

  const debounceTimer = 1200

  export default {
    components: {
      StationComponents,
      Guidelines,
      Analytes,
      DateRange,
    },
    props: {
      isGeneratingReport: Boolean,
    },
    data() {
      return {
        debounceTimer,
        guidelines: [],
        stationsMediaTypes: new StationMediaTypeGrouping(),
        isInitialized: false,
        loadingStates: {
          stations: false,
          analytes: false,
          dateRange: false,
          guidelines: false,
          downloading: false,
          selection: false,
        },
        analyteStations: {},
        allAnalytes: [],
        analytes: {},
        currentAnalytes: {},
        availableDateRange: {
          start: null,
          end: null,
        },
        initialSelection: {
          stations: [],
          analytes: [],
          dateRange: {
            start: null,
            end: null,
          },
          guidelines: [],
          guidelinesMediaTypes: [],
          stationsMediaTypes: [],
          mediaTypes: [],
        },
        selection: {},
        stationComponents: {},
      }
    },
    computed: {
      queryId() {
        return this.$route.params.id
      },
      pageTitle() {
        return this.queryId ? 'View query' : 'New query'
      },
      allMediaTypes() {
        return this.stationsMediaTypes.allMediaTypes || []
      },
      mediaTypeMatchingStationIds() {
        const mediaTypes = this.selection.mediaTypes
        if (!mediaTypes || !mediaTypes.length) return null

        const selectedMediaTypes = new Set(mediaTypes)
        const matchingStationIds = new Set()
        const { stationIndex, mediaTypeGroups } = this.stationsMediaTypes

        for (const [stationId, groupIndex] of Object.entries(stationIndex)) {
          const group = mediaTypeGroups[groupIndex]
          if (group && group.some(mt => selectedMediaTypes.has(mt))) {
            matchingStationIds.add(Number(stationId))
          }
        }

        return matchingStationIds
      },
      filteredAnalytes() {
        const matchingStationIds = this.mediaTypeMatchingStationIds
        if (!matchingStationIds) return this.analytes

        const filtered = {}
        for (const [key, analyte] of Object.entries(this.analytes)) {
          if (!analyte.StationIds) continue
          for (const stationId of analyte.StationIds) {
            if (matchingStationIds.has(stationId)) {
              filtered[key] = analyte
              break
            }
          }
        }
        return filtered
      },
      filteredAllAnalytes() {
        const matchingStationIds = this.mediaTypeMatchingStationIds
        if (!matchingStationIds) return this.allAnalytes

        return this.allAnalytes.filter(item => {
          const analyte = this.analytes[item.AnalyteName]
          if (!analyte || !analyte.StationIds) return false
          for (const stationId of analyte.StationIds) {
            if (matchingStationIds.has(stationId)) return true
          }
          return false
        })
      },
      currentDateRange() {
        if (!this.selection.analytes.length) {
          return this.availableDateRange
        }

        return analytesDateRange(this.selection.analytes)
      },
      disabledStates() {
        let disabled = {
          stations: false,
          analytes: false,
          dateRange: false,
          guidelines: false,
        }

        if (!this.isInitialized || this.loadingStates.downloading) {
          Object.keys(disabled).forEach(key => {
            disabled[key] = true
          })

          return disabled
        }

        if (this.loadingStates.analytes) {
          disabled.analytes = true
          disabled.dateRange = true
        }

        if (this.loadingStates.stations) {
          disabled.stations = true
          disabled.guidelines = true
        }

        if (this.loadingStates.dateRange) {
          disabled.analytes = true
        }

        return disabled
      },
      debouncedUpdate() {
        return debounce(async function exec() {
          await this.update()
          this.loadingStates.analytes = false
          this.loadingStates.dateRange = false
        }, this.debounceTimer)
      },
      debouncedAnalytesSelectionUpdate() {
        return debounce(function exec(value) {
          this.selection.analytes = value
        }, this.debounceTimer)
      },
    },
    watch: {
      'selection.stations'() {
        if (!this.isInitialized || this.loadingStates.selection) return
        this.loadingStates.analytes = true
        this.triggerUpdate()
        this.emitSelection()
      },
      'selection.dateRange'() {
        if (!this.isInitialized || this.loadingStates.selection) return
        this.loadingStates.dateRange = true
        this.triggerUpdate()
        this.emitSelection()
      },
      'selection.analytes'() {
        if (!this.isInitialized || this.loadingStates.selection) return
        this.emitSelection()
      },
      'selection.guidelines'() {
        if (!this.isInitialized || this.loadingStates.selection) return
        this.emitSelection()
      },
      'selection.mediaTypes'() {
        if (!this.isInitialized || this.loadingStates.selection) return
        this.emitSelection()
      },
      allMediaTypes() {
        this.$emit('update:allMediaTypes', this.allMediaTypes)
      },
      loadingStates: {
        immediate: true,
        handler(value) {
          this.$emit('update:loading', value)
        },
      },
      isGeneratingReport(value) {
        this.loadingStates.downloading = value
      },
      isInitialized(value) {
        this.$emit('update:isInitialized', value)
      },
    },
    methods: {
      ...app.mapActions(['setSnackbar']),
      emitSelection() {
        this.$emit('update:selection', this.selection)
      },
      async loadSelection({
        stationIds,
        analyteNames,
        guidelineIds,
        dateRange,
        mediaTypes,
      }, selectionOptions) {
        this.loadingStates.selection = true
        this.debounceTimer = 0

        if (mediaTypes !== undefined) {
          this.selection.mediaTypes = mediaTypes
        }

        this.$refs.stationComponents.selectStationIds(stationIds)
        this.$refs.analytes.selectAnalyteNames(analyteNames)
        this.$refs.guidelines.selectGuidelineIds(guidelineIds)
        this.$refs.dateRange.selectDateRange(dateRange, selectionOptions)

        await this.$nextTick()
        this.emitSelection()
        await this.$nextTick()

        await this.update({
          dateRange,
          stations: stationIds,
        })

        this.loadingStates.selection = false
        this.debounceTimer = debounceTimer
      },
      setMediaTypes(mediaTypes) {
        this.selection.mediaTypes = mediaTypes
      },
      setDefaultMediaTypes() {
        if (this.allMediaTypes.length && !this.selection.mediaTypes.length) {
          if (this.allMediaTypes.includes('Water')) {
            this.selection.mediaTypes = ['Water']
          } else {
            this.selection.mediaTypes = [this.allMediaTypes[0]]
          }
        }
      },
      updateAnalytesSelection(value) {
        this.loadingStates.stations = true
        this.debouncedAnalytesSelectionUpdate(value)
      },
      triggerUpdate() {
        this.debouncedUpdate()
      },
      async update(selection) {
        let requestData = selection || this.makeRequestData(this.selection)
        let analytes = await this.downloadData(requestData)
        this.analytes = Object.freeze(analytes)
        this.updateDateRange(analytes)
      },
      updateDateRange(analytesIndex) {
        let analytes = Object.values(analytesIndex)
        if (!analytes || !analytes.length) return

        this.availableDateRange.start = analytes[0].FirstSampleDate
        this.availableDateRange.end = analytes[0].LastSampleDate

        this.availableDateRange = analytes.reduce((dateRange, current) => {
          if (
            current.FirstSampleDate &&
            current.FirstSampleDate.isBefore(dateRange.start)
          ) {
            dateRange.start = current.FirstSampleDate
          }
          if (
            current.LastSampleDate &&
            current.LastSampleDate.isAfter(dateRange.end)
          ) {
            dateRange.end = current.LastSampleDate
          }

          return dateRange
        }, this.availableDateRange)
      },
      async initializeAnalytes(analytes) {
        this.allAnalytes = Object.entries(analytes).map(([, analyte]) => {
          return {
            DisplayName: analyte.DisplayName,
            AnalyteName: analyte.AnalyteName,
          }
        })

        this.analyteStations = Object.freeze(analytes)
        this.analytes = Object.freeze(analytes)

        this.updateDateRange(analytes)
      },
      makeRequestData(selection) {
        const dateRange = selection.dateRange && selection.dateRange.start && selection.dateRange.end
          ? selection.dateRange
          : this.currentDateRange
        return {
          dateRange,
          stations: selection.stations.map(station => station.id),
          mediaTypes: selection.mediaTypes,
        }
      },
      async downloadData(requestData) {
        try {
          let response = await this.$store.dispatch('multistation/fetchStationsAnalytes', requestData)
          return response
        } catch (error) {
          console.error(error)
          this.setSnackbar({
            icon: 'error',
            text: error,
            color: 'error',
          })
        }
      },
      async fetchAnalytesData() {
        this.loadingStates.analytes = true
        let analytes = await this.downloadData()

        this.initializeAnalytes(analytes)
        this.loadingStates.analytes = false
      },
      async fetchStationsGuidelines() {
        try {
          let response = await this.$store.dispatch('multistation/fetchStationsGuidelines')

          this.guidelines = response.Guidelines
          this.stationsMediaTypes = response.StationMediaTypes
          console.log('fsg', response)
        } catch (error) {
          console.error(error)
          this.setSnackbar({
            icon: 'error',
            text: error,
            color: 'error',
          })
        }
      },
      async fetchData() {
        const a = this.fetchStationsGuidelines()
        const b = this.fetchAnalytesData()
        await Promise.all([a, b])
        this.setDefaultMediaTypes()
        this.isInitialized = true
        this.emitSelection()
      },
      resetSelection() {
        this.selection = JSON.parse(JSON.stringify(this.initialSelection))
      },
    },
    created() {
      this.resetSelection()
    },
    mounted() {
      this.fetchData()
    },
  }
</script>